import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { APIList } from '../../../../../../../../../typings/API.interface';
import { APIAccountCliKey } from '../../../../../../../../../typings/CliClient.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { isUnauthorizedError } from '../../../../../../client/Hookdeck';
import Text from '../../../../../common/base/Text';
import { useDialog } from '../../../../../common/Dialog';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import SessionsSection from './SessionsSection';
import { CliCommand, getDeviceName } from './utils';

// The API returns at most 250 sessions per page, newest first. Anyone with more can still revoke
// them all with "Revoke all".
const CLI_KEYS_LIMIT = 250;

// Refresh the list while the page is open so "Active now" follows `hookdeck listen` starting
// and stopping. SWR skips the refresh while the tab is hidden.
const REFRESH_INTERVAL_MS = 30_000;
const SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: true,
  refreshInterval: REFRESH_INTERVAL_MS,
  // SWR stops polling a list while its last request failed, so a failed refresh is retried on
  // the same schedule. Retrying can't fix being signed out or not allowed, so those stop.
  onErrorRetry: (error, _key, _config, revalidate, revalidate_opts) => {
    if (isUnauthorizedError(error)) {
      return;
    }
    setTimeout(() => revalidate(revalidate_opts), REFRESH_INTERVAL_MS);
  }
};

// Each `hookdeck login` is a session here. The server still calls them CLI keys. Tokens made by
// hand in the dashboard get their own section on this page later.
const AccountSessionsAndTokens: React.FC = () => {
  const $ = _c(18);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.session.listCliKeys({
      limit: CLI_KEYS_LIMIT
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.session) {
    t1 = () => HookdeckAPI.session.listCliKeys({
      limit: CLI_KEYS_LIMIT
    });
    $[1] = HookdeckAPI.session;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: cli_keys,
    error: cli_keys_error,
    mutate: mutate_cli_keys
  } = useSWR(t0, t1, SWR_OPTIONS);
  let t2;
  if ($[3] !== HookdeckAPI.session || $[4] !== addToast || $[5] !== mutate_cli_keys || $[6] !== showDialog) {
    t2 = (cli_id, device_name) => showDialog(() => HookdeckAPI.session.revokeCliKey(cli_id).then(() => {
      addToast("success", "Session revoked");
      return mutate_cli_keys();
    }).catch(() => {
      addToast("error", "The session could not be revoked, please try again.");
    }), undefined, {
      title: "Revoke session",
      message: <Text>The CLI on{" "}<Text as="span" semi={true}>{getDeviceName(device_name)}</Text>{" "}will be logged out, and any running <CliCommand>hookdeck listen</CliCommand> there will stop. This can't be undone.</Text>,
      submit_label: "Revoke",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true
    });
    $[3] = HookdeckAPI.session;
    $[4] = addToast;
    $[5] = mutate_cli_keys;
    $[6] = showDialog;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onRevoke = t2;
  let t3;
  if ($[8] !== HookdeckAPI.session || $[9] !== addToast || $[10] !== mutate_cli_keys || $[11] !== showDialog) {
    t3 = () => showDialog(() => HookdeckAPI.session.revokeAllCliKeys().then(t4 => {
      const {
        count
      } = t4;
      addToast("success", count === 1 ? "Revoked 1 session" : `Revoked ${count} sessions`);
      return mutate_cli_keys();
    }).catch(() => {
      addToast("error", "Your sessions could not be revoked, please try again.");
    }), undefined, {
      title: "Revoke all sessions",
      message: <Text>Every CLI logged in with your account will be logged out, and any running{" "}<CliCommand>hookdeck listen</CliCommand> will stop. Run{" "}<CliCommand>hookdeck login</CliCommand> to use the CLI again.</Text>,
      submit_label: "Revoke all",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true
    });
    $[8] = HookdeckAPI.session;
    $[9] = addToast;
    $[10] = mutate_cli_keys;
    $[11] = showDialog;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const onRevokeAll = t3;
  const t4 = !!cli_keys_error;
  let t5;
  if ($[13] !== cli_keys || $[14] !== onRevoke || $[15] !== onRevokeAll || $[16] !== t4) {
    t5 = <Div><SessionsSection cli_keys={cli_keys} has_error={t4} page_limit={CLI_KEYS_LIMIT} onRevoke={onRevoke} onRevokeAll={onRevokeAll} /></Div>;
    $[13] = cli_keys;
    $[14] = onRevoke;
    $[15] = onRevokeAll;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export default AccountSessionsAndTokens;